/**
 * AssetActionsContext.jsx — Phase 8 (2026-02).
 *
 * Replaces the Phase 6/6.5 `window.__afterAssetCreated` + `window.__openArtAssetModal`
 * pair. A React context that lets any Art-Dept surface open the shared
 * `AssetModal` and register a one-shot post-save hook.
 *
 * Usage:
 *   const { openAssetModal } = useAssetActions();
 *   openAssetModal({
 *     prefill: { category: 'scene_prop', linked_scene_ids: [sid] },
 *     onCreated: async (saved) => { await linkChip(chipId, saved.id); },
 *   });
 *
 * The provider must wrap the ArtDepartmentPage tree; the modal itself is
 * rendered inside the provider so a single instance is shared.
 *
 * Backwards compat: keeps `window.__openArtAssetModal` as a thin bridge
 * for external callers (e.g. the persistent header button) until they
 * are migrated in later phases. The `window.__afterAssetCreated` bridge
 * is REMOVED — all internal callers use the context's `onCreated`.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import AssetModal from './AssetModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

export function useAssetActions() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAssetActions must be used inside <AssetActionsProvider>');
  return v;
}

export function AssetActionsProvider({ projectId, token, children }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [asset, setAsset] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [locations, setLocations] = useState([]);
  const onCreatedRef = useRef(null);

  // Fetch scene + location metadata for the modal typeaheads (once per project).
  useEffect(() => {
    if (!projectId || !token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    (async () => {
      try {
        const [br, locs] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/breakdown`, h),
          axios.get(`${API}/projects/${projectId}/art/locations`, h).catch(() => ({ data: [] })),
        ]);
        const list = Array.isArray(br.data) ? br.data : (br.data?.scenes || []);
        setScenes(list.map(s => ({ id: s.id, scene_number: s.scene_number })));
        setLocations(locs.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [projectId, token]);

  const openAssetModal = useCallback(({ mode: m = 'create', prefill: pf = null, asset: a = null, onCreated = null } = {}) => {
    setMode(m);
    setAsset(a);
    setPrefill(pf);
    onCreatedRef.current = onCreated;
    setOpen(true);
  }, []);

  const editAsset = useCallback((a) => openAssetModal({ mode: 'edit', asset: a }), [openAssetModal]);

  const onSaved = useCallback(async (saved) => {
    // Fire the one-shot post-save hook (if the caller registered one)
    try { if (onCreatedRef.current) await onCreatedRef.current(saved); }
    catch (e) { console.error('onCreated hook failed', e); }
    finally { onCreatedRef.current = null; }
  }, []);

  const onDeleted = useCallback(() => {}, []);
  const onClose = useCallback(() => { setOpen(false); onCreatedRef.current = null; }, []);

  // Thin window bridge for legacy header callers — deprecated;
  // internal callers should use the context directly.
  useEffect(() => {
    window.__openArtAssetModal = (pf) => openAssetModal({ prefill: pf });
    return () => { if (window.__openArtAssetModal) delete window.__openArtAssetModal; };
  }, [openAssetModal]);

  const value = useMemo(() => ({ openAssetModal, editAsset }), [openAssetModal, editAsset]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AssetModal
        open={open}
        mode={mode}
        asset={asset}
        prefill={prefill}
        projectId={projectId}
        token={token}
        scenes={scenes}
        locations={locations}
        onClose={onClose}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </Ctx.Provider>
  );
}
